import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Grid } from '@mui/system';
import { isEmpty, orderBy } from 'lodash';
import {
  CapTableDetailsModel,
  CapTablesService,
  Company,
  MeasurementDateName,
  MeasurementDatesService,
  type Scenario,
  SpreadsheetNote,
} from 'api';
import { FUND_EXIT_ENTERPRISE_ALIAS, FUND_EXIT_EQUITY_ALIAS, WATERFALL_SLUG } from 'common/constants/allocations';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { CAP_TABLE_REFERENCE_TITLE, CAP_TABLE_REFERENCE_TYPE } from 'common/constants/documents';
import { WATERFALL_PAGE_KEY } from 'common/constants/notes';
import { useFormat } from 'common/hooks';
import { useStore } from 'common/store';
import { CapTableVersionWithCompanyId, ChildCompany, CompanyInfo } from 'common/types/company';
import { UseFormatValues } from 'common/types/format';
import { UseStoreValues } from 'common/types/store';
import { CustomCompactContainer, CustomContainer } from 'components';
import WidgetDrawer from 'components/Drawer/widget-drawer/WidgetDrawer';
import { WidgetDrawerApplicationContext } from 'components/Drawer/widget-drawer/WidgetDrawer.props';
import EmptyTableMessage from 'components/EmptyTableMessage';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import LoadingIndicator from 'components/LoadingIndicator';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import LayoutContext, { useLayoutContext } from 'context/LayoutContext';
import { EvaluatedCell, ScalarSpreadsheetConfigDataInterface } from 'models/interfaces/spreadsheet-types/core-types';
import { NeedsFundOwnershipFirst } from 'pages/Allocation/allocations/components';
import { getValidSecurities, useGenerateChildrenCompaniesCapTablesVersions } from 'pages/CapTable/cap-table/utilities';
import { DownloadWaterfall, WaterfallTables } from 'pages/Waterfall/components';
import { WeightedShareValue } from 'pages/Waterfall/components/FundFirmDistribution/FundFirmDistribution.props';
import WaterfallContext from 'pages/Waterfall/data/WaterfallContext/WaterfallContext';
import {
  CAP_TABLE_ALIAS,
  COMPANY_ALIAS,
  EXIT_DATE_ALIAS,
  EXIT_ENTERPRISE_VALUE_ALIAS,
  EXIT_EQUITY_ALIAS,
  LESS_DEBT_ALIAS,
  MEASUREMENT_DATE_ALIAS,
  PLUS_CASH_ALIAS,
} from 'pages/Waterfall/SpreadsheetConfig/constants';
import {
  FundExchangeRateContext
} from 'pages/Waterfall/SpreadsheetConfig/FundExchangeRate/FundExchangeRateContext/FundExchangeRateContext';
import {
  useFundExchangeRateSheet
} from 'pages/Waterfall/SpreadsheetConfig/FundExchangeRate/useCreateFundExchangeRateSheet/useFundExchangeRateSheet';
import {
  useFundExchangeRate
} from 'pages/Waterfall/SpreadsheetConfig/FundExchangeRate/useFundExchangeRate/useFundExchangeRate';
import useFundInvestmentSheetConfig
  from 'pages/Waterfall/SpreadsheetConfig/FundInvestmentSheetConfig/useFundInvestmentSheetConfig/useFundInvestmentSheetConfig';
import {
  AggregatedFundOwnershipDetail
} from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/FundSummaryRowConfig/WaterfallFundSummaryRowConfig.types';
import {
  useWaterfallFundSummarySheetConfig
} from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/useFundSummarySheetConfig/useWaterfallFundSummarySheetConfig';
import {
  DEFAULT_COL_FOR_KEY
} from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/WaterfallFundSummaryParser/WaterfallFundSummaryParser.constants';
import useCreateInputSheet
  from 'pages/Waterfall/SpreadsheetConfig/InputSheetConfig/useCreateInputSheet/useCreateInputSheet';
import useWaterfallSheetConfig
  from 'pages/Waterfall/SpreadsheetConfig/WaterfallSheetConfig/useWaterfallSheetConfig/useWaterfallSheetConfig';
import { HandleScenarioValuesAndSetDataParams } from 'pages/Waterfall/types';
import { WaterfallDisplayProps } from 'pages/Waterfall/WaterfallDisplay/WaterfallDisplay.props';
import { CaptableService } from 'services';
import { useCompanyMeasurementDateFilter, useDocuments } from 'services/hooks';
import { useGetAllocationStatusBreadcrumb } from 'services/hooks/allocation';
import { useGetCapTableList, useGetCompanyInfoById } from 'services/hooks/company';
import { useNotes } from 'services/hooks/notes';
import useSpreadsheetNotes from 'services/hooks/spreadsheetNotes/useSpreadsheetNotes';
import { getBreadcrumbs, toString } from 'utilities';
import RelatedEntityWaterfallSheets from '../components/RelatedEntityWaterfallSheets/RelatedEntityWaterfallSheets';
import { useWaterfallDistribution } from '../hooks/useWaterfallDistribution';
import { useWaterfallStateInitialization } from '../hooks/useWaterfallStateInitialization';
import { useWaterfallUrlParams } from '../hooks/useWaterfallUrlParams';
import { useWaterfallUrlSync } from '../hooks/useWaterfallUrlSync';
import useFirmTotalSheetConfig
  from '../SpreadsheetConfig/FirmInvestmentSheetConfig/UseFirmTotalSheetConfig/useFirmTotalSheetConfig';
import { calculateOwnersSharesValue, useRelatedEntitiesWaterfalls } from '../utilities';

const WATERFALL_CONTAINER_CLASS = `${WATERFALL_SLUG}-container`;
const WATERFALL_INPUT_CONTAINER_CLASS = `${WATERFALL_SLUG}-input-container`;
const WATERFALL_SUMMARY_CONTAINER_CLASS = `${WATERFALL_SLUG}-summary-container`;
const WATERFALL_RELATED_ENTITY_CONTAINER_CLASS = `${WATERFALL_SLUG}-related-entity-container`;

const WaterfallDisplay = ({ isFirmPage }: WaterfallDisplayProps) => {
  const { filesToSave, setFilesToSave, addReferenceForExistingDocuments } = useDocuments();
  const { notes, setNotes, notesHasChanged, saveNotes, onAddNote, onUpdateNotes, onDeleteNote,  } = useNotes();
  const {spreadsheetNotes, setSpreadsheetNotes, saveSpreadsheetNotes, hasChanged: spreadsheetNoteHasChanged, handleOnDeleteNote: handleOnDeleteSpreadsheetNote } = useSpreadsheetNotes()
  const { parsedQuery, urlParams, updateUrlParams, clearUrlParams } = useWaterfallUrlParams(isFirmPage);
  const [capTableListFromHook, getCapTableList, clearCapTableList] = useGetCapTableList();
  const getCompanyInfoById = useGetCompanyInfoById();
  const { selectedMeasurementDate, MDDialogue, measurementDates } = useCompanyMeasurementDateFilter();
  const [{ companyInfo, firmInfo, fundList, companyList }] = useStore() as UseStoreValues;
  const [companyMeasurementDates, setCompanyMeasurementDates] = useState<MeasurementDateName[]>([]);
  const { id: companyId, child_companies } = companyInfo as CompanyInfo;
  const [captableDetail, setCaptableDetail] = useState<CapTableDetailsModel | null>(null);
  const currentCaptableRequestRef = useRef<number | null>(null);
  const hasProcessedInitialLoadRef = useRef(false);
  const [updatedWeightedShareValues, setUpdatedWeightedShareValues] = useState<WeightedShareValue[] | undefined>();
  const [childrenCapTables, setChildrenCapTables] = useState<CapTableVersionWithCompanyId[]>([]);
  const fundInvestmentsSheetsNamesRef = useRef<string[]>([]);
  const mainCapTableRef = useRef<number | null>(null);
  const mainFundOwnershipRef = useRef<CapTableDetailsModel['fund_ownership'] | null>(null);

  const {
    updateCompanyUrl,
    updateCapTableUrl,
    updateMeasurementDateUrl,
    updateExitDateUrl,
    updateEquityValueUrl,
    updateEnterpriseValueUrl,
    updateCashUrl,
    updateDebtUrl,
    parseUrlParams,
  } = useWaterfallUrlSync({
    isFirmPage: isFirmPage ?? false,
    updateUrlParams,
    companyList,
    capTableList: capTableListFromHook,
    companyMeasurementDates,
  });

  const { updatePageActions } = useLayoutContext();

  const {
    selectedCompanyId,
    companySelectedMeasurementDate,
    selectedCaptable,
    equityValue,
    enterpriseValue,
    exitDate,
    isInitializedFromUrl,
    isInitializingFromUrl,
    handleCompanyChange: handleCompanyChangeFromHook,
    handleMeasurementDateChange: handleMeasurementDateChangeFromHook,
    handleCapTableChange: handleCapTableChangeFromHook,
    handleExitDateChange: handleExitDateChangeFromHook,
    handleEquityValueChange: handleEquityValueChangeFromHook,
    handleEnterpriseValueChange: handleEnterpriseValueFromHook,
    handleCashChange: handleCashChangeFromHook,
    handleDebtChange: handleDebtChangeFromHook,
    plusCash,
    lessDebt,
    plusCashIsFromFinancials,
    lessDebtIsFromFinancials,
    calculatedEquityValue,
    isFinancialsLoading,
  } = useWaterfallStateInitialization({
    companyId,
    urlParams,
    capTableList: capTableListFromHook,
    companyMeasurementDates,
    parseUrlParams,
    isFirmPage,
    clearUrlParams,
    updateCompanyUrl,
    updateUrlParams,
    getCapTableList,
    selectedMeasurementDate,
    parsedQuery,
  });

  const saveDrawer = useCallback(() => {

    // Save files in widget
    if (!isEmpty(filesToSave) && companySelectedMeasurementDate && selectedCaptable) {
      addReferenceForExistingDocuments(
        Number(companySelectedMeasurementDate.id),
        filesToSave,
        Number(selectedCaptable),
        CAP_TABLE_REFERENCE_TYPE
      );
    }
      
    if (!isEmpty(notes) && notesHasChanged) {
      saveNotes();
    }

    if (!isEmpty(spreadsheetNotes) && spreadsheetNoteHasChanged) {
      saveSpreadsheetNotes()
    }
  }, [
    filesToSave, 
    companySelectedMeasurementDate, 
    selectedCaptable, 
    notes, 
    notesHasChanged, 
    spreadsheetNotes, 
    spreadsheetNoteHasChanged, 
    addReferenceForExistingDocuments, 
    saveNotes, 
    saveSpreadsheetNotes
  ]);

  // Set page actions
  useEffect(() => {
    if (companySelectedMeasurementDate?.cmd_id) {
      updatePageActions?.({
        mainAction: {
          id: 'waterfall',
          label: 'Save Notes & Documents',
          isActive: false,
          callback: saveDrawer,
          isLock: false,
        },
      });
    }

    return () => {
      updatePageActions?.(null);
    };
  }, [companySelectedMeasurementDate, updatePageActions, saveDrawer]);


  const [findIrrMap, setFindIrrMap] = useState<{ [key: number]: number }>({});

  // Get childrenCompanies dynamically since companyInfo might be empty
  const childrenCompanies = useMemo(() => {
    if (child_companies) {
      return child_companies;
    }
    return (companyList ?? []).find(c => c.company_id === selectedCompanyId)?.child_companies ?? null;
  }, [child_companies, companyList, selectedCompanyId]);

  // Define handleScenarioValuesAndSetData after hooks are initialized
  const handleScenarioValuesAndSetData = useCallback(
    ({ scenarioValues }: HandleScenarioValuesAndSetDataParams) => {
      const { present_values: presentValues } = scenarioValues;
      const sortedPresentValues = orderBy(presentValues, ['security.id'], ['desc']);

      // Get childrenCompanies dynamically since companyInfo might be empty
      const currentChildrenCompanies =
        child_companies || ((companyList ?? []).find(c => c.company_id === selectedCompanyId)?.child_companies ?? null);

      if (captableDetail && currentChildrenCompanies && fundList && captableDetail?.fund_ownership) {
        // Use the current cap table's fund ownership data
        const fundOwnershipRef = captableDetail.fund_ownership;

        const ownersSharesValues = calculateOwnersSharesValue({
          sortedPresentValues,
          capTableDetail: captableDetail,
          fundList,
          childrenCompanies: currentChildrenCompanies,
          childrenCapTables,
          mainFundOwnershipRef: { current: fundOwnershipRef },
        });
        setUpdatedWeightedShareValues(ownersSharesValues);
      }
    },
    [captableDetail, child_companies, companyList, selectedCompanyId, fundList, childrenCapTables]
  );

  // Initialize distribution hook
  const { scenarios, setScenarios, isLoading, setIsLoading } = useWaterfallDistribution({
    selectedCaptable,
    equityValue: calculatedEquityValue,
    exitDate,
    captableDetail,
    capTableList: capTableListFromHook,
    handleScenarioValuesAndSetData,
    isInitializedFromUrl,
    isInitializingFromUrl,
    selectedCompanyId,
  });

  const isSelectedPortfolioPrimaryCapTable = useMemo(() => {
    if (!selectedCaptable || isEmpty(capTableListFromHook)) {
      return false;
    }
    return Boolean(
      (capTableListFromHook ?? []).some(captable => captable.id === selectedCaptable && captable.is_primary)
    );
  }, [selectedCaptable, capTableListFromHook]);

  const shouldRunRelatedEntitiesWaterfall = useMemo(
    () => isSelectedPortfolioPrimaryCapTable && (childrenCompanies ?? []).length > 0,
    [isSelectedPortfolioPrimaryCapTable, childrenCompanies]
  );

  const { generateChildrenCompaniesCapTablesVersions } = useGenerateChildrenCompaniesCapTablesVersions(
    companyList,
    selectedMeasurementDate?.date ?? companySelectedMeasurementDate?.date,
    selectedCompanyId ? Number(selectedCompanyId) : undefined
  );

  const selectedCompanyInformation = useMemo(() => {
    if (isSelectedPortfolioPrimaryCapTable) {
      return (companyList ?? []).find(ct => ct.company_id === selectedCompanyId);
    }
    const childCompanyId = childrenCapTables.find(ct => ct.id === selectedCaptable);

    return (childrenCompanies ?? []).find(child => Number(child.company_id) === Number(childCompanyId?.company_id));
  }, [
    companyList,
    selectedCompanyId,
    isSelectedPortfolioPrimaryCapTable,
    childrenCapTables,
    childrenCompanies,
    selectedCaptable,
  ]);

  useEffect(() => {
    const fetchAndSetChildrenCapTables = async () => {
      const result = await generateChildrenCompaniesCapTablesVersions();
      const allChildrenCapTables = Object.values(result).flat();
      setChildrenCapTables(allChildrenCapTables.filter(ct => ct.is_primary));
    };
    fetchAndSetChildrenCapTables();
  }, [generateChildrenCompaniesCapTablesVersions]);

  const { setPageBreadcrumbs } = useContext(LayoutContext);

  const {
    format: fundFormat,
    formatDispatch: fundFormatDispatch,
    fundExchangeRateProvider,
    fundExchangeRateSheet,
    isAdditionalCurrencySelected
  } = useFundExchangeRate({
    page: CAP_TABLE_CURRENCY_PAGE,
    units: REGULAR_UNIT,
    measurementDate: companySelectedMeasurementDate,
    marketDate: exitDate ?? null,
    captable: captableDetail,
  });

  const [format, formatDispatch] = useFormat({
    page: CAP_TABLE_CURRENCY_PAGE,
    units: REGULAR_UNIT,
    measurementDate: companySelectedMeasurementDate,
  }) as unknown as UseFormatValues;

  const { allocationStatusBreadcrumb } = useGetAllocationStatusBreadcrumb();

  // Fetch company info when selectedCompanyId changes and companyInfo is empty
  useEffect(() => {
    if (selectedCompanyId && (isEmpty(companyInfo) || selectedCompanyId !== companyInfo?.id)) {
      getCompanyInfoById(selectedCompanyId);
    }
  }, [selectedCompanyId, companyInfo, getCompanyInfoById]);

  useEffect(() => {
    if (selectedCompanyId) {
      setIsLoading(true);
      MeasurementDatesService.apiMeasurementDatesCompanyRead(toString(selectedCompanyId))
        .then(data => {
          setCompanyMeasurementDates(data);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [selectedCompanyId, setIsLoading]);

  // Clear cap table list when company changes to avoid showing outdated data
  useEffect(() => {
    if (companyId || (isFirmPage && !isInitializedFromUrl)) {
      clearCapTableList();
    }
  }, [companyId, clearCapTableList, isFirmPage, isInitializedFromUrl]);

  // Set main cap table ref when cap table list changes
  useEffect(() => {
    if (capTableListFromHook && capTableListFromHook.length > 0) {
      const primaryCapTable = capTableListFromHook.find(captable => captable.is_primary);
      mainCapTableRef.current = primaryCapTable?.id ?? null;
    }
  }, [capTableListFromHook]);

  const getCaptableDetail = useCallback(
    async (captableId: number) => {
      // Track the current request to prevent stale responses
      const requestId = captableId;
      currentCaptableRequestRef.current = requestId;

      const captSvc = new CaptableService();
      const data = (await captSvc.getCaptableById(captableId)) as unknown as CapTableDetailsModel;

      // Only set the data if this is still the current request and matches the selected cap table
      // This prevents stale responses while allowing legitimate data updates
      if (currentCaptableRequestRef.current === requestId && selectedCaptable === captableId) {
        setCaptableDetail(data);
        if (mainCapTableRef.current === captableId) {
          mainFundOwnershipRef.current = data.fund_ownership;
        }
      }
    },
    [selectedCaptable]
  );

  useEffect(() => {
    // Only reset captableDetail when the company or selected cap table changes
    // This allows measurement date changes to persist the selected cap table
    // But don't reset during initial load to preserve deeplink data
    if (hasProcessedInitialLoadRef.current) {
      setCaptableDetail(null);
      // Clear the current request ref to prevent stale responses
      currentCaptableRequestRef.current = null;
    }
  }, [selectedCompanyId, selectedCaptable, isInitializedFromUrl]);

  // Mark initial load as complete after URL parsing is done AND cap table data is loaded
  useEffect(() => {
    if (isInitializedFromUrl && capTableListFromHook && capTableListFromHook.length > 0) {
      hasProcessedInitialLoadRef.current = true;
    }
  }, [isInitializedFromUrl, capTableListFromHook]);

  const validSecurities = useMemo(() => {
    if (captableDetail) {
      const securities = getValidSecurities(captableDetail);
      return securities;
    }
    return [];
  }, [captableDetail]);

  useEffect(() => {
    if (!isEmpty(companyInfo) && setPageBreadcrumbs && firmInfo && companyInfo) {
      setPageBreadcrumbs(
        getBreadcrumbs({
          firmInfo,
          companyInfo: companyInfo as unknown as Company,
          breadcrumbsTitle: 'Waterfall',
          extraBreadcrumbs: [allocationStatusBreadcrumb],
        })
      );
    }
    return () => setPageBreadcrumbs?.(null);
  }, [companyInfo, firmInfo, setPageBreadcrumbs, allocationStatusBreadcrumb]);

  useEffect(() => {
    if (selectedCaptable) {
      getCaptableDetail(selectedCaptable);
    }
  }, [selectedCaptable, getCaptableDetail]);

  const firmInputData = useMemo(() => {
    if (isFirmPage) {
      return {
        companyId: selectedCompanyId,
        measurementDateId: companySelectedMeasurementDate?.id,
      };
    }
  }, [isFirmPage, selectedCompanyId, companySelectedMeasurementDate]);

  const { sheetConfig: waterfallSheet } = useWaterfallSheetConfig({
    scenarioObject: scenarios,
    validSecurities,
    companyName: selectedCompanyInformation?.company_name ?? '',
  });
  const { spreadsheets: fundInvestmentSheets } = useFundInvestmentSheetConfig({
    weightedShareValues: updatedWeightedShareValues,
    validSecurities,
    idToAssociateTo: null,
  });

  const {
    ownersShareValuesMap,
    relatedEntitiesWaterfalls,
    childrenSheets,
    registerChildrenSheets,
    resetChildrenSheets,
  } = useRelatedEntitiesWaterfalls({
    updatedWeightedShareValues,
    childrenCapTables,
    childrenCompanies: childrenCompanies ?? [],
    exitDate: exitDate ?? '',
    fundList: fundList ?? [],
    mainFundOwnershipRef,
  });

  const { sheetConfig: summarySheetConfig } = useWaterfallFundSummarySheetConfig({
    captable: captableDetail,
    fundList,
    childrenCompanies,
    calculatedEquityValue,
    findIrrMap,
  });

  const { spreadsheet: firmTotalSheet } = useFirmTotalSheetConfig({
    weightedShareValues: updatedWeightedShareValues,
    securityCount: validSecurities.length,
    investmentsInPortfolioSheets: fundInvestmentSheets,
    allInvestmentsSheets: [...fundInvestmentSheets, ...Object.values(childrenSheets).flat()],
    isSelectedPortfolioPrimaryCapTable,
    fundInvestmentsSheetsNamesRef,
    waterfallHasRunAtLeastOnce: Boolean(scenarios),
  });
  const inputSheet = useCreateInputSheet({
    exitDate,
    capTableId: selectedCaptable,
    equityValue,
    enterpriseValue,
    firmOptions: firmInputData,
    childrenCapTables,
    parentCapTables: capTableListFromHook,
    fundInvestmentsSheetsNamesRef,
    plusCashIsFromFinancials,
    lessDebtIsFromFinancials,
    plusCash,
    lessDebt,
    isAdditionalCurrencySelected
  });

  const sheets = useMemo(() => {
    if (shouldRunRelatedEntitiesWaterfall && Object.values(childrenSheets).flat().length > 0) {
      return [
        inputSheet,
        fundExchangeRateSheet,
        waterfallSheet,
        ...fundInvestmentSheets,
        ...Object.values(childrenSheets).flat(),
        firmTotalSheet,
        summarySheetConfig,
      ].filter(sheet => !!sheet);
    }
    return [
      inputSheet,
      fundExchangeRateSheet,
      waterfallSheet,
      ...fundInvestmentSheets,
      firmTotalSheet,
      summarySheetConfig,
    ].filter(sheet => !!sheet);
  }, [
    fundExchangeRateSheet,
    inputSheet,
    waterfallSheet,
    fundInvestmentSheets,
    childrenSheets,
    firmTotalSheet,
    shouldRunRelatedEntitiesWaterfall,
    summarySheetConfig,
  ]);

  const { cells: workbookCells, data: workbookData, onChange, workbookContextValue } = useWorkbook(sheets);

  useFundExchangeRateSheet({ cells: workbookCells, onChange, exchangeRate: fundExchangeRateProvider.exchangeRate });

  const irrCache = useRef<{ [key: string]: Promise<number | null> }>({});

  const getIrrData = useCallback(async (captableId: number, fundId: number, unrealizedValue: number, exit: string) => {
    const cacheKey = `${captableId}-${fundId}-${unrealizedValue}-${exit}`;

    // Return existing promise if it exists
    if (cacheKey in irrCache.current) {
      return irrCache.current[cacheKey];
    }

    // Create and cache the promise
    const promise = CapTablesService.apiCapTablesGrossIrrRead(captableId, fundId, unrealizedValue.toString(), exit)
      .then(response => response.gross_irr)
      .catch(error => {
        // Remove failed requests from cache so they can be retried
        delete irrCache.current[cacheKey];
        throw error;
      });

    // Cache the promise immediately
    irrCache.current[cacheKey] = promise;

    return promise;
  }, []);

  const isInputsLoaded = useMemo(() => {
    if (isFirmPage) {
      return !!companyList?.length && !isFinancialsLoading;
    }
    return !isFinancialsLoading;
  }, [isFirmPage, companyList, isFinancialsLoading]);

  useEffect(() => {
    if (workbookCells?.waterfallFundSummary && selectedCaptable && exitDate && isInputsLoaded) {
      const waterfallFundSummaryCells = Object.values(
        workbookCells.waterfallFundSummary ?? {}
      ) as EvaluatedCell<AggregatedFundOwnershipDetail>[];
      const equityValueCells = waterfallFundSummaryCells.filter(
        cell => cell.alias === 'exitProceeds' && cell.value !== 0
      );
      if (equityValueCells.every(cell => !cell.invalidExpr)) {
        equityValueCells.forEach(cell => {
          getIrrData(selectedCaptable, cell.data?.fund as number, cell.value as number, exitDate).then(grossIrr => {
            const relatedCell = workbookCells.waterfallFundSummary[DEFAULT_COL_FOR_KEY.irr + cell.rowNumber];
            if (relatedCell) {
              setFindIrrMap(prev => {
                if (cell.data?.fund && prev[cell.data.fund] !== grossIrr) {
                  return { ...prev, [relatedCell.rowNumber]: grossIrr ?? 0 };
                }
                return prev;
              });
              onChange(relatedCell, grossIrr);
            }
          });
        });
      }
    }
  }, [workbookCells, selectedCaptable, onChange, exitDate, getIrrData, isInputsLoaded]);

  // Handler functions for different cell aliases
  // Updated handler functions that use the new hooks
  const handleExitDateChange = useCallback(
    (value: any) => {
      handleExitDateChangeFromHook(value as string);
      updateExitDateUrl(value as string);
    },
    [handleExitDateChangeFromHook, updateExitDateUrl]
  );

  const handleEquityValueChange = useCallback(
    (value: any) => {
      handleEquityValueChangeFromHook(value as number);
      updateEquityValueUrl(value as number);
    },
    [handleEquityValueChangeFromHook, updateEquityValueUrl]
  );

  const handleEnterpriseValueChange = useCallback(
    (value: any) => {
      handleEnterpriseValueFromHook(value as number);
      updateEnterpriseValueUrl(value as number);
    },
    [handleEnterpriseValueFromHook, updateEnterpriseValueUrl]
  );

  const handleCashChange = useCallback(
    (value: any) => {
      // value can be number or empty to clear
      const num = value === '' || value === null || value === undefined ? null : (value as number);
      handleCashChangeFromHook(num);
      updateCashUrl(num);
    },
    [handleCashChangeFromHook, updateCashUrl]
  );

  const handleDebtChange = useCallback(
    (value: any) => {
      const num = value === '' || value === null || value === undefined ? null : (value as number);
      handleDebtChangeFromHook(num);
      updateDebtUrl(num);
    },
    [handleDebtChangeFromHook, updateDebtUrl]
  );

  const handleCapTableChange = useCallback(
    (value: any) => {
      const newCapTableId = Number(value);
      handleCapTableChangeFromHook(newCapTableId);
      setScenarios(undefined);
      resetChildrenSheets();
      updateCapTableUrl(newCapTableId);
    },
    [handleCapTableChangeFromHook, updateCapTableUrl, resetChildrenSheets, setScenarios]
  );

  const handleCompanyChange = useCallback(
    (value: any) => {
      const newCompanyId = Number(value);
      handleCompanyChangeFromHook(newCompanyId);
      setScenarios(undefined);
      resetChildrenSheets();
      updateCompanyUrl(newCompanyId);
    },
    [handleCompanyChangeFromHook, updateCompanyUrl, resetChildrenSheets, setScenarios]
  );

  const handleMeasurementDateChange = useCallback(
    (value: any) => {
      handleMeasurementDateChangeFromHook(Number(value));
      setScenarios(undefined);
      // Don't reset the selected cap table when measurement date changes
      // The cap table will be validated in the useEffect above
      if (value && companyMeasurementDates) {
        const newMeasurementDate = companyMeasurementDates.find(md => md.id === Number(value));
        if (newMeasurementDate) {
          updateMeasurementDateUrl(newMeasurementDate);
        }
      }
    },
    [handleMeasurementDateChangeFromHook, updateMeasurementDateUrl, companyMeasurementDates, setScenarios]
  );

  const handleFundExitEnterpriseValueChange = useCallback(() => {
    const enterpriseValueCell = Object.values(workbookCells?.InputSheet ?? {}).find(
      c => c.alias === EXIT_ENTERPRISE_VALUE_ALIAS
    );
    if (enterpriseValueCell) {
      handleEnterpriseValueChange(enterpriseValueCell.value);
    }
  }, [handleEnterpriseValueChange, workbookCells]);

  const handleFundExitEquityValueChange = useCallback(() => {
    const equityValueCell = Object.values(workbookCells?.InputSheet ?? {}).find(c => c.alias === EXIT_EQUITY_ALIAS);
    if (equityValueCell) {
      handleEquityValueChange(equityValueCell.value);
    }
  }, [handleEquityValueChange, workbookCells]);

  const onWorkbookChange = useCallback(
    (cell: EvaluatedCell, value: any) => {
      onChange(cell, value);
      switch (cell.alias) {
        case EXIT_DATE_ALIAS:
          handleExitDateChange(value);
          break;
        case EXIT_EQUITY_ALIAS:
          handleEquityValueChange(value);
          break;
        case FUND_EXIT_ENTERPRISE_ALIAS:
          handleFundExitEnterpriseValueChange();
          break;
        case FUND_EXIT_EQUITY_ALIAS:
          handleFundExitEquityValueChange();
          break;
        case EXIT_ENTERPRISE_VALUE_ALIAS:
          handleEnterpriseValueChange(value);
          break;
        case PLUS_CASH_ALIAS:
          handleCashChange(value);
          break;
        case LESS_DEBT_ALIAS:
          handleDebtChange(value);
          break;
        case CAP_TABLE_ALIAS:
          handleCapTableChange(value);
          break;
        case COMPANY_ALIAS:
          handleCompanyChange(value);
          break;
        case MEASUREMENT_DATE_ALIAS:
          if (companyMeasurementDates) {
            handleMeasurementDateChange(value);
          }
          break;
        default:
          break;
      }
    },
    [
      onChange,
      handleExitDateChange,
      handleEquityValueChange,
      handleEnterpriseValueChange,
      handleCapTableChange,
      handleCompanyChange,
      handleMeasurementDateChange,
      companyMeasurementDates,
      handleFundExitEnterpriseValueChange,
      handleFundExitEquityValueChange,
      handleCashChange,
      handleDebtChange,
    ]
  );

  // Initialize updatedWeightedShareValues on page load with default values
  const initializeUpdatedWeightedShareValues = useCallback(() => {
    if (
      captableDetail &&
      childrenCompanies &&
      fundList &&
      validSecurities.length > 0 &&
      captableDetail?.fund_ownership
    ) {
      // Create default present values based on valid securities
      const defaultPresentValues = validSecurities.map(security => ({
        security: { id: security.id },
        value: 0,
      })) as any[];

      const sortedPresentValues = orderBy(defaultPresentValues, ['security.id'], ['desc']);

      // Use the current cap table's fund ownership data
      const fundOwnershipRef = captableDetail.fund_ownership;

      const ownersSharesValues = calculateOwnersSharesValue({
        sortedPresentValues,
        capTableDetail: captableDetail,
        fundList,
        childrenCompanies,
        childrenCapTables,
        mainFundOwnershipRef: { current: fundOwnershipRef },
      });

      setUpdatedWeightedShareValues(ownersSharesValues);
    }
  }, [captableDetail, childrenCompanies, fundList, validSecurities, childrenCapTables]);

  // Initialize updatedWeightedShareValues on page load
  useEffect(() => {
    if (
      captableDetail &&
      childrenCompanies &&
      fundList &&
      validSecurities.length > 0 &&
      captableDetail?.fund_ownership
    ) {
      initializeUpdatedWeightedShareValues();
    }
  }, [captableDetail, childrenCompanies, fundList, validSecurities, initializeUpdatedWeightedShareValues]);

  // Set loading state for firm page
  useEffect(() => {
    if (isFirmPage) {
      setIsLoading(!companyList?.length);
    }
  }, [companyList, isFirmPage, setIsLoading]);

  const waterfallContextValues = useMemo(
    () => ({
      companyList: companyList ?? [],
      measurementDates: companyMeasurementDates,
      workbookData,
      onChange,
      format,
      formatDispatch,
    }),
    [companyList, companyMeasurementDates, workbookData, onChange, format, formatDispatch]
  );

  return (
    <>
      <WorkbookContext.Provider value={workbookContextValue}>
        <FundExchangeRateContext.Provider value={fundExchangeRateProvider}>
          <WaterfallContext.Provider value={waterfallContextValues}>
            <CustomContainer className={WATERFALL_CONTAINER_CLASS}>
              <CustomContainer>
                {workbookData && inputSheet?.data && isInputsLoaded && (
                  <>
                    <Grid className={WATERFALL_INPUT_CONTAINER_CLASS} id="waterfall-inputs" size={{ xs: 12 }}>
                      <ScalarSpreadsheet
                        {...inputSheet}
                        format={fundFormat}
                        showToolbar
                        formatDispatch={fundFormatDispatch}
                        onChange={onWorkbookChange}
                        disableUnsavedChanges
                        disableVerticalScroll
                        sheet={inputSheet}
                      />
                    </Grid>

                    {summarySheetConfig && (
                      <Grid className={WATERFALL_SUMMARY_CONTAINER_CLASS} size={{ xs: 12 }}>
                        <ScalarSpreadsheet
                          {...summarySheetConfig}
                          format={format}
                          showToolbar
                          formatDispatch={formatDispatch}
                          onChange={onWorkbookChange}
                          disableUnsavedChanges
                          disableVerticalScroll
                          sheet={summarySheetConfig}
                        />{' '}
                      </Grid>
                    )}
                  </>
                )}
              </CustomContainer>

              {waterfallSheet && workbookData && updatedWeightedShareValues && (
                <CustomContainer>
                  <CustomCompactContainer>
                    <CustomContainer>
                      <Grid size={{ xs: 12 }}>
                        <WaterfallTables
                          waterfallSheet={waterfallSheet as ScalarSpreadsheetConfigDataInterface}
                          fundInvestmentsSheets={fundInvestmentSheets}
                          firmTotalSheet={
                            Object.entries(ownersShareValuesMap ?? {}).flat().length ? undefined : firmTotalSheet
                          }
                        />
                      </Grid>

                      {shouldRunRelatedEntitiesWaterfall &&
                      ownersShareValuesMap &&
                      Object.entries(ownersShareValuesMap).map(([relatedEntityId, sharesValue]) => (
                        <Grid className={WATERFALL_RELATED_ENTITY_CONTAINER_CLASS} key={relatedEntityId}>
                          <RelatedEntityWaterfallSheets
                            relatedEntity={
                              (childrenCompanies ?? []).find(
                                child => Number(child.company_id) === Number(relatedEntityId)
                              ) ?? ({} as ChildCompany)
                            }
                            scenarioResult={relatedEntitiesWaterfalls?.[Number(relatedEntityId)] as Scenario}
                            childrenCapTables={childrenCapTables}
                            ownedSharesValue={sharesValue}
                            registerChildrenSheets={registerChildrenSheets}
                          />
                        </Grid>
                      ))}
                    </CustomContainer>

                    {exitDate &&
                  (equityValue || calculatedEquityValue) &&
                  companySelectedMeasurementDate &&
                  selectedCompanyInformation &&
                  selectedCaptable ? (
                        <DownloadWaterfall
                          exitDate={exitDate}
                          equityValue={equityValue ?? (calculatedEquityValue as number)}
                          companyMeasurementDate={companySelectedMeasurementDate}
                          selectedCapTableId={selectedCaptable}
                          selectedCompanyId={Number(selectedCompanyInformation.company_id)}
                        />
                      ) : null}
                  </CustomCompactContainer>
                </CustomContainer>
              )}

              <LoadingIndicator isLoading={isLoading} />

              {validSecurities?.length === 0 && scenarios !== undefined && selectedCaptable && (
                <EmptyTableMessage
                  title="The selected company has an incomplete cap table"
                  tableTerms={{
                    tableName: 'Cap table',
                    pluralColumnName: 'Securities',
                    columnName: 'Security',
                  }}
                />
              )}

              {updatedWeightedShareValues && isEmpty(updatedWeightedShareValues) && (
                <NeedsFundOwnershipFirst
                  measurementDates={measurementDates}
                  selectedMeasurementDate={selectedMeasurementDate}
                  title="No fund ownership defined for the cap table"
                  tagline="Add fund ownership to see its share values"
                />
              )}
              <MDDialogue />
            </CustomContainer>
          </WaterfallContext.Provider>
        </FundExchangeRateContext.Provider>
      </WorkbookContext.Provider>
      {companySelectedMeasurementDate && selectedCaptable && (

        <WidgetDrawer
          applicationContext={WidgetDrawerApplicationContext.CAP_TABLE}
          notesProps={{
            pageType: SpreadsheetNote.page_type.WTRFL,
            pageTypeKey: WATERFALL_PAGE_KEY,
            pageTypeId: companySelectedMeasurementDate.cmd_id,
            notes,
            setNotes,
            onAddNote,
            onUpdateNotes,
            onDeleteNote,
            isDisabled: false,
          }}
          documentReferencesProps={{
            selectedMeasurementDate: companySelectedMeasurementDate,
            referenceType: CAP_TABLE_REFERENCE_TYPE,
            currentPage: CAP_TABLE_REFERENCE_TITLE,
            referencedFeatureId: selectedCaptable,
            filesToSave,
            setFilesToSave,
            isDisplayingRowNumbers: false,
            isDisabled: false,
          }}
          spreadsheetNotesProps={{
            pageTypeId: companySelectedMeasurementDate.cmd_id as number,
            pageType: SpreadsheetNote.page_type.WTRFL,
            pageTypeKey: WATERFALL_PAGE_KEY,
            notes: spreadsheetNotes,
            setNotes: setSpreadsheetNotes,
            saveNotes: () => {
              saveSpreadsheetNotes()
            },
            handleOnDeleteNote: handleOnDeleteSpreadsheetNote,
          }}
        />
      )}
    </>
  );
};

export default WaterfallDisplay;
